/**
 * DYC Analytics Tracker
 * Lightweight client-side analytics for Shopify theme
 * Version: 1.0.0
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    endpoint: 'https://dyc-analytics.vercel.app/api/ingest',
    batchSize: 10,
    batchInterval: 15000, // 15 seconds - optimized for performance
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
  };
  
  // State
  let sessionId = getOrCreateSessionId();
  let eventQueue = [];
  let batchTimer = null;
  let maxScrollDepth = 0;
  let pageViewStartTime = Date.now();
  
  // Initialize
  function init() {
    // Track page view
    trackEvent('view', {
      category: 'pageview',
      action: 'load',
      label: document.title,
    });
    
    // Setup event listeners
    setupClickTracking();
    setupScrollTracking();
    setupAISearchTracking();
    setupWidgetTracking();
    
    // Track page unload
    window.addEventListener('beforeunload', handlePageUnload);
    
    // Start batch timer
    startBatchTimer();
  }
  
  // Session Management
  function getOrCreateSessionId() {
    const storageKey = 'dyc_session_id';
    const timestampKey = 'dyc_session_timestamp';
    
    const existingId = localStorage.getItem(storageKey);
    const timestamp = localStorage.getItem(timestampKey);
    
    if (existingId && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CONFIG.sessionTimeout) {
        // Update timestamp
        localStorage.setItem(timestampKey, Date.now().toString());
        return existingId;
      }
    }
    
    // Create new session
    const newId = generateId();
    localStorage.setItem(storageKey, newId);
    localStorage.setItem(timestampKey, Date.now().toString());
    return newId;
  }
  
  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
  
  // Event Tracking
  function trackEvent(type, data) {
    const event = {
      id: generateId(),
      sessionId: sessionId,
      timestamp: Date.now(),
      type: type,
      page: {
        url: window.location.href,
        path: window.location.pathname,
        title: document.title,
        referrer: document.referrer || 'direct',
      },
      user: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          pixelRatio: window.devicePixelRatio || 1,
        },
        device: getDeviceType(),
        os: getOperatingSystem(),
        browser: getBrowser(),
        language: navigator.language || navigator.userLanguage || 'unknown',
        connection: getConnectionType(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      },
      data: data,
    };
    
    eventQueue.push(event);
    
    // Send immediately for critical events
    if (type === 'purchase' || type === 'add_to_cart') {
      sendBatch();
    } else if (eventQueue.length >= CONFIG.batchSize) {
      sendBatch();
    }
  }
  
  // Click Tracking
  function setupClickTracking() {
    document.addEventListener('click', function(e) {
      const target = e.target;
      const element = getElementInfo(target);
      
      // Track all clicks
      trackEvent('click', {
        x: e.clientX,
        y: e.clientY,
        element: element,
        target: target.href || undefined,
      });
      
      // Track specific interactions
      if (target.matches('[data-track-widget]')) {
        trackWidgetClick(target);
      }
      
      if (target.matches('.ai-quick-add, .ai-chip, .ai-view-all')) {
        trackAISearchInteraction(target);
      }
      
      if (target.matches('[name="add"], .product-form__submit')) {
        trackAddToCart(target);
      }
    }, true);
  }
  
  function getElementInfo(element) {
    return {
      tag: element.tagName.toLowerCase(),
      id: element.id || undefined,
      className: element.className || undefined,
      text: element.textContent?.trim().substring(0, 100) || undefined,
      selector: getCSSSelector(element),
    };
  }
  
  function getCSSSelector(element) {
    if (element.id) return '#' + element.id;
    
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.split(' ').filter(c => c && !c.match(/^is-|^has-/));
      if (classes.length > 0) {
        return element.tagName.toLowerCase() + '.' + classes.slice(0, 2).join('.');
      }
    }
    
    return element.tagName.toLowerCase();
  }
  
  // Scroll Tracking
  function setupScrollTracking() {
    let scrollTimeout;
    
    window.addEventListener('scroll', function() {
      clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(function() {
        const scrollDepth = getScrollDepth();

        if (scrollDepth > maxScrollDepth) {
          maxScrollDepth = scrollDepth;

          // Track milestones
          if (scrollDepth >= 25 && maxScrollDepth < 25) trackScrollMilestone(25);
          if (scrollDepth >= 50 && maxScrollDepth < 50) trackScrollMilestone(50);
          if (scrollDepth >= 75 && maxScrollDepth < 75) trackScrollMilestone(75);
          if (scrollDepth >= 90 && maxScrollDepth < 90) trackScrollMilestone(90);
        }
      }, 300); // Increased from 150ms for better Android performance
    });
  }
  
  function getScrollDepth() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    return Math.round(((scrollTop + windowHeight) / documentHeight) * 100);
  }
  
  function trackScrollMilestone(depth) {
    trackEvent('scroll', {
      depth: depth,
      maxDepth: maxScrollDepth,
      direction: 'down',
    });
  }
  
  // AI Search Tracking
  function setupAISearchTracking() {
    // Track AI search queries
    const aiSearchInput = document.querySelector('.ai-search-input');
    if (aiSearchInput) {
      let searchTimeout;
      
      aiSearchInput.addEventListener('input', function(e) {
        clearTimeout(searchTimeout);
        
        searchTimeout = setTimeout(function() {
          const query = e.target.value.trim();
          if (query.length >= 3) {
            trackEvent('search', {
              query: query,
              results: 0, // Will be updated by response
              type: 'ai',
            });
          }
        }, 1000);
      });
    }
  }
  
  function trackAISearchInteraction(element) {
    trackEvent('custom', {
      category: 'ai_search',
      action: element.classList.contains('ai-quick-add') ? 'quick_add' : 
              element.classList.contains('ai-chip') ? 'chip_click' : 'view_all',
      label: element.textContent?.trim() || 'unknown',
    });
  }
  
  // Widget Tracking
  function setupWidgetTracking() {
    // Track carousel/slider interactions
    const carousels = document.querySelectorAll('.slider-component, [data-track-widget="carousel"]');
    
    carousels.forEach(function(carousel) {
      const buttons = carousel.querySelectorAll('.slider-button, .slider-counter__link');
      
      buttons.forEach(function(button) {
        button.addEventListener('click', function() {
          trackEvent('custom', {
            category: 'widget',
            action: 'carousel_navigation',
            label: carousel.dataset.trackWidget || 'carousel',
            metadata: {
              direction: button.classList.contains('slider-button--next') ? 'next' : 'prev',
            },
          });
        });
      });
    });
  }
  
  function trackWidgetClick(element) {
    trackEvent('custom', {
      category: 'widget',
      action: 'click',
      label: element.dataset.trackWidget || 'unknown',
      metadata: {
        text: element.textContent?.trim() || undefined,
      },
    });
  }
  
  // Add to Cart Tracking
  function trackAddToCart(element) {
    const form = element.closest('form');
    const productId = form?.querySelector('[name="id"]')?.value;
    
    trackEvent('add_to_cart', {
      category: 'ecommerce',
      action: 'add_to_cart',
      label: productId || 'unknown',
      metadata: {
        source: window.location.pathname.includes('/products/') ? 'product_page' : 'other',
      },
    });
  }
  
  // Page Unload
  function handlePageUnload() {
    const timeOnPage = Date.now() - pageViewStartTime;
    
    trackEvent('custom', {
      category: 'engagement',
      action: 'time_on_page',
      value: timeOnPage,
      metadata: {
        scrollDepth: maxScrollDepth,
      },
    });
    
    // Send remaining events
    sendBatch(true);
  }
  
  // Batch Sending
  function startBatchTimer() {
    batchTimer = setInterval(function() {
      if (eventQueue.length > 0) {
        sendBatch();
      }
    }, CONFIG.batchInterval);
  }
  
  function sendBatch(useBeacon = false) {
    if (eventQueue.length === 0) return;
    
    const events = eventQueue.splice(0, CONFIG.batchSize);
    const payload = JSON.stringify({ events: events });
    
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(CONFIG.endpoint, payload);
    } else {
      fetch(CONFIG.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: payload,
        keepalive: true,
      }).catch(function(error) {
        console.error('DYC Analytics error:', error);
        // Re-queue events on failure
        eventQueue.unshift(...events);
      });
    }
  }
  
  // Device Detection
  function getDeviceType() {
    const ua = navigator.userAgent.toLowerCase();
    
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    
    if (/mobile|android|iphone|ipod|iemobile|blackberry|kindle|silk-accelerated|(hpw|web)os|opera m(obi|ini)/i.test(ua)) {
      return 'mobile';
    }
    
    return 'desktop';
  }
  
  // Operating System Detection
  function getOperatingSystem() {
    const ua = navigator.userAgent;
    const platform = navigator.platform || '';
    
    // iOS Detection
    if (/iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      const match = ua.match(/OS (\d+)_(\d+)_?(\d+)?/);
      const version = match ? `${match[1]}.${match[2]}` : 'unknown';
      return `iOS ${version}`;
    }
    
    // Android Detection
    if (/Android/.test(ua)) {
      const match = ua.match(/Android (\d+\.?\d*)/);
      const version = match ? match[1] : 'unknown';
      return `Android ${version}`;
    }
    
    // Windows Detection
    if (/Windows/.test(ua)) {
      if (/Windows NT 10/.test(ua)) return 'Windows 10';
      if (/Windows NT 6.3/.test(ua)) return 'Windows 8.1';
      if (/Windows NT 6.2/.test(ua)) return 'Windows 8';
      if (/Windows NT 6.1/.test(ua)) return 'Windows 7';
      return 'Windows';
    }
    
    // macOS Detection
    if (/Mac OS X/.test(ua)) {
      const match = ua.match(/Mac OS X (\d+)[._](\d+)/);
      const version = match ? `${match[1]}.${match[2]}` : 'unknown';
      return `macOS ${version}`;
    }
    
    // Linux Detection
    if (/Linux/.test(ua) && !/Android/.test(ua)) {
      return 'Linux';
    }
    
    return 'Unknown';
  }
  
  // Browser Detection
  function getBrowser() {
    const ua = navigator.userAgent;
    
    // Edge
    if (/Edg\//.test(ua)) {
      const match = ua.match(/Edg\/(\d+)/);
      return match ? `Edge ${match[1]}` : 'Edge';
    }
    
    // Chrome
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) {
      const match = ua.match(/Chrome\/(\d+)/);
      return match ? `Chrome ${match[1]}` : 'Chrome';
    }
    
    // Safari
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) {
      const match = ua.match(/Version\/(\d+)/);
      return match ? `Safari ${match[1]}` : 'Safari';
    }
    
    // Firefox
    if (/Firefox\//.test(ua)) {
      const match = ua.match(/Firefox\/(\d+)/);
      return match ? `Firefox ${match[1]}` : 'Firefox';
    }
    
    // Opera
    if (/OPR\//.test(ua)) {
      const match = ua.match(/OPR\/(\d+)/);
      return match ? `Opera ${match[1]}` : 'Opera';
    }
    
    // Samsung Browser
    if (/SamsungBrowser\//.test(ua)) {
      const match = ua.match(/SamsungBrowser\/(\d+)/);
      return match ? `Samsung ${match[1]}` : 'Samsung Browser';
    }
    
    return 'Unknown';
  }
  
  // Connection Type Detection
  function getConnectionType() {
    if (!navigator.connection && !navigator.mozConnection && !navigator.webkitConnection) {
      return 'unknown';
    }
    
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (connection.effectiveType) {
      return connection.effectiveType; // '4g', '3g', '2g', 'slow-2g'
    }
    
    if (connection.type) {
      return connection.type;
    }
    
    return 'unknown';
  }
  
  // Public API
  window.DYCAnalytics = {
    track: trackEvent,
    trackCustom: function(category, action, value, metadata) {
      const data = {
        category: category,
        action: action,
      };
      
      // Add value if provided
      if (value !== undefined && value !== null) {
        data.value = value;
      }
      
      // Add label for backward compatibility
      if (typeof value === 'string') {
        data.label = value;
      }
      
      // Merge metadata if provided
      if (metadata && typeof metadata === 'object') {
        Object.assign(data, metadata);
      }
      
      trackEvent('custom', data);
    },
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
